import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, Settings, User as UserIcon, ChevronDown, ChevronUp, Key, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PermissionsPanel } from '../components/users/PermissionsPanel';
import { PERMISSION_GROUPS, ALL_PERMISSIONS } from '../utils/permissions';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [myPasswordDialogOpen, setMyPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'staff'
  });
  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [myPasswordForm, setMyPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Set default permissions based on role
      const permissions = {};
      ALL_PERMISSIONS.forEach(p => {
        permissions[p.key] = p.key === 'delete_records' ? formData.role === 'admin' : true;
      });

      await axios.post(`${API_URL}/auth/register`, {
        ...formData,
        permissions
      });
      toast.success('User created successfully');
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_URL}/users/${userId}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handlePermissionChange = async (userId, permissionKey, checked) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    // Don't allow changing admin's own permissions
    if (userId === currentUser?.id) {
      toast.error("You cannot modify your own permissions");
      return;
    }

    const updatedPermissions = {
      ...user.permissions,
      [permissionKey]: checked
    };

    try {
      await axios.put(`${API_URL}/users/${userId}`, {
        permissions: updatedPermissions
      });
      
      // Update local state
      setUsers(users.map(u => 
        u.id === userId 
          ? { ...u, permissions: updatedPermissions }
          : u
      ));
      
      toast.success('Permission updated');
    } catch (error) {
      toast.error('Failed to update permission');
    }
  };

  const handleGrantAllPermissions = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const allGranted = {};
    ALL_PERMISSIONS.forEach(p => { allGranted[p.key] = true; });

    try {
      await axios.put(`${API_URL}/users/${userId}`, {
        permissions: allGranted,
        role: 'admin'
      });
      
      setUsers(users.map(u => 
        u.id === userId 
          ? { ...u, permissions: allGranted, role: 'admin' }
          : u
      ));
      
      toast.success('All permissions granted');
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const handleRevokeAllPermissions = async (userId) => {
    if (userId === currentUser?.id) {
      toast.error("You cannot revoke your own permissions");
      return;
    }

    const allRevoked = {};
    ALL_PERMISSIONS.forEach(p => { allRevoked[p.key] = false; });
    // Keep at least dashboard access
    allRevoked['view_dashboard'] = true;

    try {
      await axios.put(`${API_URL}/users/${userId}`, {
        permissions: allRevoked,
        role: 'staff'
      });
      
      setUsers(users.map(u => 
        u.id === userId 
          ? { ...u, permissions: allRevoked, role: 'staff' }
          : u
      ));
      
      toast.success('Permissions revoked');
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const handleSelectAllInGroup = async (userId, groupName, selectAll) => {
    const user = users.find(u => u.id === userId);
    if (!user || userId === currentUser?.id) return;

    const group = PERMISSION_GROUPS.find(g => g.name === groupName);
    if (!group) return;

    const updatedPermissions = { ...user.permissions };
    group.permissions.forEach(p => {
      updatedPermissions[p.key] = selectAll;
    });

    try {
      await axios.put(`${API_URL}/users/${userId}`, {
        permissions: updatedPermissions
      });
      
      setUsers(users.map(u => 
        u.id === userId 
          ? { ...u, permissions: updatedPermissions }
          : u
      ));
      
      toast.success(`${groupName} permissions ${selectAll ? 'granted' : 'revoked'}`);
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const getGroupPermissionCount = (user, groupName) => {
    const group = PERMISSION_GROUPS.find(g => g.name === groupName);
    if (!group || !user.permissions) return { granted: 0, total: 0 };
    
    const granted = group.permissions.filter(p => user.permissions[p.key]).length;
    return { granted, total: group.permissions.length };
  };

  const toggleUserExpand = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'staff'
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API_URL}/users/${selectedUser.id}/password`, {
        new_password: passwordForm.new_password
      });
      toast.success(`Password changed for ${selectedUser.name}`);
      setPasswordDialogOpen(false);
      setPasswordForm({ new_password: '', confirm_password: '' });
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleChangeMyPassword = async (e) => {
    e.preventDefault();
    if (myPasswordForm.new_password !== myPasswordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (myPasswordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: myPasswordForm.current_password,
        new_password: myPasswordForm.new_password
      });
      toast.success('Your password has been changed');
      setMyPasswordDialogOpen(false);
      setMyPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setPasswordForm({ new_password: '', confirm_password: '' });
    setPasswordDialogOpen(true);
  };

  const getPermissionCount = (user) => {
    if (!user.permissions) return 0;
    return Object.values(user.permissions).filter(Boolean).length;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Users & Permissions</h1>
          <p className="text-muted-foreground">Manage system users and their access rights</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" data-testid="add-user-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="user-dialog">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <Input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Role</label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (Full Access)</SelectItem>
                    <SelectItem value="staff">Staff (Limited Access)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.role === 'admin' ? 'Admin users have all permissions including delete.' : 'Staff can access all features except delete. You can customize permissions after creation.'}
                </p>
              </div>
              <Button type="submit" className="w-full" data-testid="user-submit-btn">
                Create User
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {users.length === 0 ? (
          <div className="bg-white rounded-xl border border-border shadow-sm p-12 text-center text-muted-foreground">
            No users yet. Add your first user to get started.
          </div>
        ) : (
          users.map((user) => (
            <div key={user.id} className="bg-white rounded-xl border border-border shadow-sm overflow-hidden" data-testid="user-card">
              {/* User Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => toggleUserExpand(user.id)}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    user.role === 'admin' ? 'bg-primary/10 text-primary' : 'bg-blue-100 text-blue-600'
                  }`}>
                    {user.role === 'admin' ? <Shield className="w-5 h-5" /> : <UserIcon className="w-5 h-5" />}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-foreground">{user.name}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        user.role === 'admin' ? 'bg-primary/10 text-primary' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{getPermissionCount(user)}</span>/{ALL_PERMISSIONS.length} permissions
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); openPasswordDialog(user); }}
                    title="Change Password"
                    data-testid={`change-password-${user.id}`}
                  >
                    <Key className="w-4 h-4 text-blue-600" />
                  </Button>
                  {user.id !== currentUser?.id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(user.id); }}
                      data-testid="delete-user-btn"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                  {expandedUsers[user.id] ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                </div>
              </div>

              {/* Expanded Permissions Section */}
              {expandedUsers[user.id] && (
                <div className="border-t border-border p-4 bg-muted/20">
                  <PermissionsPanel
                    user={user}
                    currentUserId={currentUser?.id}
                    onPermissionChange={(permKey, checked) => handlePermissionChange(user.id, permKey, checked)}
                    onSelectAllInGroup={(groupName, value) => handleSelectAllInGroup(user.id, groupName, value)}
                    onGrantAll={() => handleGrantAllPermissions(user.id)}
                    onRevokeAll={() => handleRevokeAllPermissions(user.id)}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Change My Password Button - Fixed at bottom right */}
      <div className="fixed bottom-20 right-6">
        <Button
          onClick={() => setMyPasswordDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg"
          data-testid="change-my-password-btn"
        >
          <Key className="w-4 h-4 mr-2" />
          Change My Password
        </Button>
      </div>

      {/* Password Change Dialog (Admin changing user's password) */}
      <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
        setPasswordDialogOpen(open);
        if (!open) {
          setPasswordForm({ new_password: '', confirm_password: '' });
          setSelectedUser(null);
        }
      }}>
        <DialogContent data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle>Change Password for {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">New Password</label>
              <Input
                type="password"
                required
                minLength={6}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Minimum 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Confirm Password</label>
              <Input
                type="password"
                required
                minLength={6}
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="change-password-submit">
              Change Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change My Password Dialog */}
      <Dialog open={myPasswordDialogOpen} onOpenChange={(open) => {
        setMyPasswordDialogOpen(open);
        if (!open) {
          setMyPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        }
      }}>
        <DialogContent data-testid="my-password-dialog">
          <DialogHeader>
            <DialogTitle>Change Your Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangeMyPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Current Password</label>
              <Input
                type="password"
                required
                value={myPasswordForm.current_password}
                onChange={(e) => setMyPasswordForm({ ...myPasswordForm, current_password: e.target.value })}
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">New Password</label>
              <Input
                type="password"
                required
                minLength={6}
                value={myPasswordForm.new_password}
                onChange={(e) => setMyPasswordForm({ ...myPasswordForm, new_password: e.target.value })}
                placeholder="Minimum 6 characters"
                data-testid="my-new-password-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Confirm New Password</label>
              <Input
                type="password"
                required
                minLength={6}
                value={myPasswordForm.confirm_password}
                onChange={(e) => setMyPasswordForm({ ...myPasswordForm, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                data-testid="my-confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="my-password-submit">
              Update Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
